/*
 * File: CurrentUsers.jsx
 * Project: lets-talk-web
 *
 * Created by Morgan Rivera on March 27, 2026 at 1:25 PM
 *
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useRef, useState
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Services
import { currentUsers } from '../../../services/admin';


// Utilities
import { createStateLocale } from '../../../utilities/locale';

// Components
import {
	Meta,
	AppNavigation,
	Typography
} from '../../../components';

// Styles
import * as S from './CurrentUsers.styles';

// Constants

/**
 * Component
 */

const CurrentUsers = ({ meta, locale, data }) => {

	// Set state
	const [componentData, setComponentData] = useState(null);
	const [pageStatus, setPageStatus] = useState('idle');
	const [users, setUsers] = useState([]);


	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Create reference for components
	const isMounted = useRef(true);

	// Initialize component data function
	const fetchDataForPage = async () => {

		// Update page status
		setPageStatus('loading');

		// NV

		console.log(componentData);
		setComponentData({});
		setPageStatus('success');

	};

	// Handle get current users

	async function handleCurrentUsers() {
		currentUsers({}).then((response) => {
			const userArray = response.data?.data || [];
			setUsers(userArray);
		});
	}

	// Handle actions on app component state change
	useEffect(() => {

		// Ensure initial page loading is not complete
		if (pageStatus === 'idle') {

			// Fetch data state for page
			fetchDataForPage();
		}
	}, [pageStatus]);

	// Handle component initialization
	useEffect(() => {

		// Set state
		isMounted.current = true;

		// get current users
		handleCurrentUsers();

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Get user journey data for unit and modules
	const getJourneyData = (journey = []) => {
		if (!journey || journey.length === 0) return [];

		return journey.map((unit, index) => {
			const modules = unit?.modules || [];

			const totalModules = modules.length;
			const completedModules = modules.filter((m) => m?.isCompleted || m?.status === 'completed').length;

			const isUnitComplete = totalModules > 0 && modules.every((m) => m?.isCompleted || m?.status === 'completed');

			return {
				label: isUnitComplete ? '●' : '○',
				hoverText: `Unit ${index + 1}: ${completedModules}/${totalModules} modules completed`
			};
		});
	};


	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} locale={stateLocale} />

			{/* Component Content */}
			<AppNavigation data={data}>
				<S.Wrapper left right>

					{/* Title Widget */}
					<S.TitleWidget>
						<Typography tag="h4" weight="medium">List of Current Users</Typography>
					</S.TitleWidget>

					{/* Table */}

					<table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
						<thead>
							<tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
								<th><Typography tag="span" weight="bold">Name</Typography></th>
								<th><Typography tag="span" weight="bold">Signup Date</Typography></th>
								<th><Typography tag="span" weight="bold">Email</Typography></th>
								<th><Typography tag="span" weight="bold">Role</Typography></th>
								{/* <th><Typography tag="span" weight="bold">Parent</Typography></th>
								<th><Typography tag="span" weight="bold">Children</Typography></th> */}
								<th><Typography tag="span" weight="bold">Journey</Typography></th>
								<th><Typography tag="span" weight="bold">Last Login</Typography></th>
							</tr>
						</thead>
						<tbody>
							{users.map((user) => (

								<tr key={user._id}>
									{/* Display Name */}
									<td><Typography tag="span">{user.name || 'N/A'}</Typography></td>
									{/* Display Date */}
									<td>
										<Typography tag="span">
											{user.createdAt
												? new Date(user.createdAt).toLocaleDateString('en-US')
												: 'N/A'}
										</Typography>
									</td>
									{/* Display Email */}
									<td><Typography tag="span">{user.email || 'N/A'}</Typography></td>
									{/* Display Role Name */}
									<td><Typography tag="span">{user.roleName || user.role?.primary || 'N/A'}</Typography></td>
									{/* Display Parent Name */}
									{/* <td><Typography tag="span">{user.parent?.name || 'N/A'}</Typography></td> */}
									{/* Display Child Name */}
									{/* <td><Typography tag="span">{user.children?.at(0)?.child?.name || 'N/A'}</Typography></td> */}
									{/* Display Journey Data */}
									<td>
										{getJourneyData(user.journey).map((unit, i) => (
											/* eslint-disable-next-line react/no-array-index-key */
											<span key={`${user._id}_u_${i}`} title={unit.hoverText}>
												{unit.label}
											</span>
										))}
									</td>
									{/* Display Last Login */}
									<td>
										<Typography tag="span">
											{user._loginHistory?.length > 0
												? new Date(user._loginHistory.at(-1).date).toLocaleString('en-US', {
													month: 'numeric',
													day: 'numeric',
													year: 'numeric',
													hour: '2-digit',
													minute: '2-digit',
													hour12: true
												})
												: 'N/A'}
										</Typography>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

CurrentUsers.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
	data: PropTypes.shape(),
};
CurrentUsers.defaultProps = {
	meta: {},
	locale: {},
	data: null
};


/**
 * Exports
 */

export default CurrentUsers;
